import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { isEmpty } from 'util/objects'
import useHelplyWidgetEnabled from 'util/hooks/useHelplyWidgetEnabled'
import { selectCurrentBillingCycle } from 'ducks/billing/selectors/selectCurrentBillingCycle'

/**
 * Opens support chat, by what `useHelplyWidgetEnabled` says this account gets. The new Helply widget:
 * `Helply('identify', …)` with the signed `helplyWidgetIdentifyToken` (on every call — each identify replaces the
 * widget session) plus the unverified attributes, then `Helply('open')`. The legacy Helply agent, for a flagged
 * account the new widget's gate leaves out: as it always was. Neither: the Groove widget.
 */
const useOpenContact = ({ left } = {}) => {
  const currentUser = useSelector(selectCurrentUser)
  const billingCycle = useSelector(selectCurrentBillingCycle)
  const helply = useHelplyWidgetEnabled()

  const openContact = useCallback(
    async e => {
      if (helply.widget && typeof window.Helply === 'function') {
        if (!isEmpty(currentUser)) {
          // Not awaited: before the widget boots the stub returns undefined, and commands never reject.
          // Email and name travel inside the signed token; they are reserved keys as attributes. The token
          // is signed with the widget's own identity secret (null when the environment has no widget yet,
          // so the identify is then anonymous: attributes only).
          window.Helply('identify', {
            ...(currentUser.helplyWidgetIdentifyToken
              ? { token: currentUser.helplyWidgetIdentifyToken }
              : {}),
            attributes: {
              'Unified-Inbox': currentUser.preferences.unified_inbox,
              'Billing-Cycle': billingCycle,
            },
          })
        }
        // identify then open: from a closed widget the identify waits for the open and runs then.
        window.Helply('open')
        if (e?.preventDefault) {
          e.preventDefault()
        }

        return
      }
      if (helply.legacy && window.helplyAgentWidget) {
        if (!isEmpty(currentUser)) {
          window.helplyAgentWidget.setContact({
            email: currentUser.email,
            firstName: currentUser.first_name,
            lastName: currentUser.last_name,
          })

          window.helplyAgentWidget.identify({
            token: currentUser.helplyIdentifyToken,
            user_metadata: {
              'Unified-Inbox': currentUser.preferences.unified_inbox,
              'Billing-Cycle': billingCycle,
            },
          })
        }
        window.helplyAgentWidget.open()
        if (e?.preventDefault) {
          e.preventDefault()
        }

        return
      }
      if (!window.groove?.widget) return
      if (e?.preventDefault) {
        e.preventDefault()
      }

      if (left) {
        await window.groove.widget.updateSettings({
          iconPosition: 'left',
        })
      }
      window.groove.widget.pushRoute('/contact')
      window.groove.widget.open()
      if (!isEmpty(currentUser)) {
        window.groove.widget.identifyContact('contact_email', currentUser.email)
        window.groove.widget.setContact({
          contact_first_name: currentUser.first_name,
          contact_last_name: currentUser.last_name,
        })
      }
    },
    [currentUser, left, helply, billingCycle]
  )

  return openContact
}

export default useOpenContact
